import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'sonner'
import * as Sentry from '@sentry/react'
import App from './App'
import { AuthProvider } from './contexts/AuthContext'
import { initAnalytics } from './lib/analytics'
import './index.css'

// ─── Posthog — captura de eventos de produto ───────────────────────────────
// Igual ao Sentry: só inicializa em produção quando a VITE_POSTHOG_KEY existe.
// O `track()` retorna no-op silenciosa nos demais casos.
initAnalytics()

// ─── Sentry — error tracking + perf monitoring ─────────────────────────────
// Inicializa apenas em produção; em dev fica desligado para não poluir o
// projeto Sentry com ruído de hot-reload. DSN vem por env (VITE_SENTRY_DSN).
// Se a DSN não estiver setada, o init é no-op (não quebra o app).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: !import.meta.env.DEV,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,        // habilitar quando quiser session replay
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      // Retry com backoff exponencial: 1s, 2s, 4s (limite 30s).
      // Recupera de Supabase wake-from-pause, blips de rede e 5xx transientes.
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
)
